import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api, { apiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { StatusBadge } from "@/components/common";
import { toast } from "sonner";

export default function ExportDialog({ open, onClose, employeeIds, formStatusFilter }) {
  const [templateId, setTemplateId] = useState("");
  const [report, setReport] = useState(null);
  const [busy, setBusy] = useState(false);

  const { data: templates = [] } = useQuery({
    queryKey: ["templates-export"], queryFn: async () => (await api.get("/exports/templates")).data, enabled: open,
  });

  const body = () => (employeeIds?.length ? { employee_ids: employeeIds } : { form_status: formStatusFilter || undefined });

  const validate = async () => {
    if (!templateId) return;
    setBusy(true);
    try {
      const { data } = await api.post("/org/export/validate", { template_id: templateId, ...body() });
      setReport(data);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const run = async (mode) => {
    setBusy(true);
    try {
      const res = await api.post("/org/export/run", { template_id: templateId, mode, ...body() }, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      const tpl = templates.find((t) => t.id === templateId);
      a.href = url; a.download = `export.${tpl?.file_type || "xlsx"}`; a.click();
      URL.revokeObjectURL(url);
      toast.success("Export downloaded");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const raw = async () => {
    setBusy(true);
    try {
      const res = await api.post("/org/export/raw", body(), { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = "raw_employee_data.xlsx"; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) { setReport(null); setTemplateId(""); onClose(); } }}>
      <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto" data-testid="export-dialog">
        <DialogHeader><DialogTitle>Export to Zoho</DialogTitle></DialogHeader>
        <p className="text-xs text-slate-500">{employeeIds?.length ? `${employeeIds.length} selected employee(s)` : `All employees${formStatusFilter ? ` (${formStatusFilter})` : ""}`}</p>
        <div>
          <Label className="text-xs">Template</Label>
          <Select value={templateId} onValueChange={(v) => { setTemplateId(v); setReport(null); }}>
            <SelectTrigger className="mt-1" data-testid="export-template-select"><SelectValue placeholder="Choose a template" /></SelectTrigger>
            <SelectContent>{templates.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <Button variant="outline" onClick={validate} disabled={!templateId || busy} data-testid="validate-export">Validate</Button>

        {report && (
          <div className="rounded-lg border border-slate-100 p-3" data-testid="validation-report">
            <p className="mb-2 text-sm"><span className="font-medium text-emerald-600">{report.valid_count} valid</span> · <span className="text-rose-500">{report.invalid_count} with issues</span></p>
            <div className="max-h-60 space-y-1 overflow-y-auto">
              {report.report.map((r) => (
                <div key={r.employee_id} className="flex items-start justify-between gap-2 text-xs" data-testid={`vr-${r.employee_id}`}>
                  <span>{r.name} {r.employee_code ? `(${r.employee_code})` : ""}</span>
                  <span className="text-right">{r.valid ? <StatusBadge status="ok" /> : <span className="text-rose-500">{r.issues.join("; ")}</span>}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        <DialogFooter className="flex-wrap gap-2">
          <Button variant="ghost" onClick={raw} disabled={busy} data-testid="export-raw">Raw data export</Button>
          <Button variant="outline" onClick={() => run("valid")} disabled={!templateId || busy} data-testid="export-valid">Valid rows only + errors</Button>
          <Button onClick={() => run("all")} disabled={!templateId || busy} data-testid="export-all">Export all</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
